import { ElementGroup } from './elementGroup';
import { EquipmentElement } from './equipmentElement';
import { EquipmentElements } from './equipmentElements';
import { Utils } from '../../../core/services/utils.service';

export interface IElementGroup {
	name: ElementGroup;
	list: Array<EquipmentElement>;
}

export class ElementsList {
	public readonly list: Array<EquipmentElement>;

	public readonly groupsOrder = [
		ElementGroup.BaseElement,
		ElementGroup.WallElement,
		ElementGroup.EquipmentElement,
		ElementGroup.MerchandisingElement
	];
	public readonly draggableElements: { front: Array<EquipmentElements>; top: Array<EquipmentElements> } = {
		front: [
			EquipmentElements.BaseRectangle,
			EquipmentElements.BaseTrapeze,
			EquipmentElements.BackWall,
			EquipmentElements.CentralPillar,
			EquipmentElements.HookWall,
			EquipmentElements.PerforatedWall,
			EquipmentElements.SideWalls,
			EquipmentElements.Showcase,
			EquipmentElements.HookPanel,
			EquipmentElements.Perforation,
			EquipmentElements.Shelf,
			EquipmentElements.AngleShelf,
			EquipmentElements.HangBar,
			EquipmentElements.Partition,
			EquipmentElements.Hook
		],
		top: [
			EquipmentElements.Box,
			EquipmentElements.SlicedBox,
			EquipmentElements.Partition
		]
	};

	constructor() {
		this.list = [
			// Bases
			new EquipmentElement({
				icon: 'elementBaseRectangle',
				name: 'RECTANGLE_BASE',
				type: EquipmentElements.BaseRectangle,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.BaseElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition],
				deniedTypes: [],
				requiredTypes: []
			}),
			new EquipmentElement({
				icon: 'elementBaseTrapeze',
				name: 'TRAPEZE_BASE',
				type: EquipmentElements.BaseTrapeze,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.BaseElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition],
				deniedTypes: [],
				requiredTypes: []
			}),
			// Wall elements
			new EquipmentElement({
				icon: 'elementBackWall',
				name: 'BACK_WALL',
				type: EquipmentElements.BackWall,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.WallElement,
				deniedRepresentations: ['top'],
				allowedTypes: [
					EquipmentElements.Shelf,
					EquipmentElements.AngleShelf,
					EquipmentElements.HangBar,
					EquipmentElements.HookPanel,
					EquipmentElements.Perforation
				],
				deniedTypes: [],
				requiredTypes: [[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze]]
			}),
			new EquipmentElement({
				icon: 'elementSideWalls',
				name: 'SIDES_WALL',
				type: EquipmentElements.SideWalls,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.WallElement,
				deniedRepresentations: ['top'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[EquipmentElements.BackWall, EquipmentElements.HookWall, EquipmentElements.PerforatedWall]
				]
			}),
			new EquipmentElement({
				icon: 'elementCentralPillar',
				name: 'CENTRAL_PILLAR',
				type: EquipmentElements.CentralPillar,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.WallElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Shelf],
				deniedTypes: [],
				requiredTypes: [[EquipmentElements.BaseRectangle]]
			}),
			new EquipmentElement({
				icon: 'elementPerforatedWall',
				name: 'PERFORATION_WALL',
				type: EquipmentElements.PerforatedWall,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.WallElement,
				deniedRepresentations: ['top'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze]]
			}),
			new EquipmentElement({
				icon: 'elementHookWall',
				name: 'HOOK_WALL',
				type: EquipmentElements.HookWall,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.WallElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Hook],
				deniedTypes: [],
				requiredTypes: [[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze]]
			}),
			new EquipmentElement({
				icon: 'elementShowcase',
				name: 'SHOWCASE',
				type: EquipmentElements.Showcase,
				behavior: 'auto',
				visible: true,
				group: ElementGroup.WallElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Shelf, EquipmentElements.AngleShelf],
				deniedTypes: [],
				requiredTypes: [[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze]]
			}),
			// Equipment elements
			new EquipmentElement({
				icon: 'elementShelf',
				name: 'SHELF',
				type: EquipmentElements.Shelf,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.EquipmentElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[EquipmentElements.BackWall, EquipmentElements.CentralPillar, EquipmentElements.Showcase]
				]
			}),
			new EquipmentElement({
				icon: 'elementShelfAngle',
				name: 'ANGLE_SHELF',
				type: EquipmentElements.AngleShelf,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.EquipmentElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Box, EquipmentElements.SlicedBox, EquipmentElements.Partition],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle],
					[EquipmentElements.BackWall, EquipmentElements.Showcase]
				]
			}),
			new EquipmentElement({
				icon: 'elementHangBar',
				name: 'HANG_BAR',
				type: EquipmentElements.HangBar,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.EquipmentElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Hook],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[EquipmentElements.BackWall]
				]
			}),
			new EquipmentElement({
				icon: 'elementHook',
				name: 'HOOK',
				type: EquipmentElements.Hook,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.EquipmentElement,
				deniedRepresentations: ['top'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[EquipmentElements.HookWall, EquipmentElements.HookPanel, EquipmentElements.HangBar]
				]
			}),
			new EquipmentElement({
				icon: 'elementHookPanel',
				name: 'HOOK_PANEL',
				type: EquipmentElements.HookPanel,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.EquipmentElement,
				deniedRepresentations: ['top'],
				allowedTypes: [EquipmentElements.Hook],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[EquipmentElements.BackWall]
				]
			}),
			new EquipmentElement({
				icon: 'elementPerforation',
				name: 'PERFORATION',
				type: EquipmentElements.Perforation,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.EquipmentElement,
				deniedRepresentations: ['top'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[EquipmentElements.BackWall]
				]
			}),
			new EquipmentElement({
				icon: '',
				name: 'HOLE',
				type: EquipmentElements.Hole,
				behavior: 'custom',
				visible: false,
				group: ElementGroup.EquipmentElement,
				deniedRepresentations: ['top'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[EquipmentElements.PerforatedWall, EquipmentElements.Perforation]
				]
			}),
			// Merchandising elements
			new EquipmentElement({
				icon: 'elementPartition',
				name: 'PARTITION',
				type: EquipmentElements.Partition,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.MerchandisingElement,
				deniedRepresentations: ['front'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[
						EquipmentElements.Shelf,
						EquipmentElements.AngleShelf,
						EquipmentElements.BaseRectangle,
						EquipmentElements.BaseTrapeze
					]
				]
			}),
			new EquipmentElement({
				icon: 'elementBox',
				name: 'BOX',
				type: EquipmentElements.Box,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.MerchandisingElement,
				deniedRepresentations: ['front'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[
						EquipmentElements.Shelf,
						EquipmentElements.AngleShelf,
						EquipmentElements.BaseRectangle,
						EquipmentElements.BaseTrapeze
					]
				]
			}),
			new EquipmentElement({
				icon: 'elementBox',
				name: 'SLICED_BOX',
				type: EquipmentElements.SlicedBox,
				behavior: 'custom',
				visible: true,
				group: ElementGroup.MerchandisingElement,
				deniedRepresentations: ['front'],
				allowedTypes: [],
				deniedTypes: [],
				requiredTypes: [
					[EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
					[
						EquipmentElements.Shelf,
						EquipmentElements.AngleShelf,
						EquipmentElements.BaseRectangle,
						EquipmentElements.BaseTrapeze
					]
				]
			})
		];
	}

	public get groups() {
		return Utils.groupBy(this.list, 'group')
			.map((group) => ({ name: group[0].group, list: group }))
			.sort(
				({ name: groupA }, { name: groupB }) =>
					this.groupsOrder.indexOf(groupA) - this.groupsOrder.indexOf(groupB)
			);
	}

	public getParent(type: EquipmentElements) {
		return this.list.find((element) => element.type === type);
	}
}
